/**
 * GAP-ORG-STRUCTURE A: organisation units, unit types, memberships and Excel import.
 * Backend: app/django-core/apps/org/ (views.py, import_views.py).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { archiveArgs } from '../../../shared/api/archiveArgs';
import { identityAdminApi } from '../../identity/api/adminApi';

const BASE = '/api/v1/org';
// What a suggestion or the ownership report is computed from.
const SUGGEST_TAGS = ['OrgPartner', 'OrgFunction', 'OrgUnit', 'OrgTree'];
const UNIT_TAGS = ['OrgUnit', 'OrgTree', 'OrgMembers'];

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

export const orgApi = createApi({
  reducerPath: 'orgApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['OrgUnit', 'OrgUnitType', 'OrgTree', 'OrgMembers', 'OrgFunction', 'OrgPartner'],
  endpoints: (builder) => ({
    getOrgTree: builder.query({
      query: () => ({ url: `${BASE}/units/tree/` }),
      providesTags: ['OrgTree'],
    }),
    getOrgUnits: builder.query({
      query: (params = {}) => ({ url: `${BASE}/units/`, params }),
      providesTags: ['OrgUnit'],
    }),
    getOrgUnit: builder.query({
      query: (id) => ({ url: `${BASE}/units/${id}/` }),
      providesTags: ['OrgUnit'],
    }),
    getOrgUnitMembers: builder.query({
      query: (id) => ({ url: `${BASE}/units/${id}/members/` }),
      providesTags: (_r, _e, id) => [{ type: 'OrgMembers', id }, 'OrgMembers'],
    }),
    createOrgUnit: builder.mutation({
      query: (body) => ({ url: `${BASE}/units/`, method: 'POST', body }),
      invalidatesTags: UNIT_TAGS,
    }),
    updateOrgUnit: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${BASE}/units/${id}/`, method: 'PATCH', body }),
      invalidatesTags: UNIT_TAGS,
    }),
    moveOrgUnit: builder.mutation({
      query: ({ id, parent }) => ({
        url: `${BASE}/units/${id}/move/`,
        method: 'POST',
        body: { parent },
      }),
      invalidatesTags: UNIT_TAGS,
    }),
    archiveOrgUnit: builder.mutation({
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `${BASE}/units/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: UNIT_TAGS,
    }),
    searchOrgPeople: builder.query({
      query: (search) => ({ url: `${BASE}/units/people/`, params: { search } }),
    }),
    getOrgUnitTypes: builder.query({
      query: () => ({ url: `${BASE}/unit-types/` }),
      providesTags: ['OrgUnitType'],
    }),
    createOrgUnitType: builder.mutation({
      query: (body) => ({ url: `${BASE}/unit-types/`, method: 'POST', body }),
      invalidatesTags: ['OrgUnitType', ...UNIT_TAGS],
    }),
    updateOrgUnitType: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${BASE}/unit-types/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['OrgUnitType', ...UNIT_TAGS],
    }),
    deleteOrgUnitType: builder.mutation({
      query: (id) => ({ url: `${BASE}/unit-types/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['OrgUnitType'],
    }),
    setMemberships: builder.mutation({
      query: (body) => ({ url: `${BASE}/memberships/`, method: 'POST', body }),
      invalidatesTags: ['OrgTree', 'OrgMembers', 'OrgUnit'],
      // The admin user list (another API slice) shows each person's units: refresh it
      // only once the units are saved, or it refetches too early and stays stale.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(identityAdminApi.util.invalidateTags(['TenantUsers']));
      },
    }),
    getOrgFunctions: builder.query({
      query: (params = {}) => ({ url: `${BASE}/functions/`, params }),
      providesTags: ['OrgFunction'],
    }),
    createOrgFunction: builder.mutation({
      query: (body) => ({ url: `${BASE}/functions/`, method: 'POST', body }),
      invalidatesTags: ['OrgFunction', 'OrgPartner'],
    }),
    updateOrgFunction: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${BASE}/functions/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['OrgFunction', 'OrgPartner'],
    }),
    deleteOrgFunction: builder.mutation({
      query: (id) => ({ url: `${BASE}/functions/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['OrgFunction', 'OrgPartner'],
    }),
    getOrgPartners: builder.query({
      query: (params = {}) => ({ url: `${BASE}/partners/`, params }),
      providesTags: ['OrgPartner'],
    }),
    createOrgPartner: builder.mutation({
      query: (body) => ({ url: `${BASE}/partners/`, method: 'POST', body }),
      invalidatesTags: ['OrgPartner'],
    }),
    updateOrgPartner: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${BASE}/partners/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['OrgPartner'],
    }),
    deleteOrgPartner: builder.mutation({
      query: (id) => ({ url: `${BASE}/partners/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['OrgPartner'],
    }),
    suggestOwner: builder.query({
      providesTags: SUGGEST_TAGS,
      query: ({ affectedUnit, owningUnit }) => ({
        url: `${BASE}/suggest-owner/`,
        params: {
          ...(affectedUnit ? { affected_unit: affectedUnit } : {}),
          owning_unit: owningUnit,
        },
      }),
    }),
    getOwnershipReport: builder.query({
      providesTags: SUGGEST_TAGS,
      query: ({ by = 'owning' } = {}) => ({ url: `${BASE}/ownership-report/`, params: { by } }),
    }),
    exportOwnershipReport: builder.mutation({
      async queryFn({ by = 'owning' } = {}, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `${BASE}/ownership-report/export/`,
          params: { by },
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        const disposition = result.meta?.response?.headers?.get('Content-Disposition') || '';
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        saveBlob(result.data, match ? match[1] : 'ownership-report.csv');
        return { data: { ok: true } };
      },
    }),
    downloadOrgImportTemplate: builder.mutation({
      async queryFn(_arg, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `${BASE}/import/template/`,
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        saveBlob(result.data, 'organisation-import-template.xlsx');
        return { data: { ok: true } };
      },
    }),
    previewOrgImport: builder.mutation({
      query: (file) => {
        const body = new FormData();
        body.append('file', file);
        return { url: `${BASE}/import/preview/`, method: 'POST', body };
      },
    }),
    commitOrgImport: builder.mutation({
      query: (token) => ({ url: `${BASE}/import/commit/`, method: 'POST', body: { token } }),
      // An import can also create functions and partner rows.
      invalidatesTags: [...UNIT_TAGS, 'OrgUnitType', 'OrgFunction', 'OrgPartner'],
    }),
  }),
});

export const {
  useGetOrgTreeQuery,
  useGetOrgUnitsQuery,
  useGetOrgUnitQuery,
  useGetOrgUnitMembersQuery,
  useSearchOrgPeopleQuery,
  useCreateOrgUnitMutation,
  useUpdateOrgUnitMutation,
  useMoveOrgUnitMutation,
  useArchiveOrgUnitMutation,
  useGetOrgUnitTypesQuery,
  useCreateOrgUnitTypeMutation,
  useUpdateOrgUnitTypeMutation,
  useDeleteOrgUnitTypeMutation,
  useSetMembershipsMutation,
  useDownloadOrgImportTemplateMutation,
  usePreviewOrgImportMutation,
  useCommitOrgImportMutation,
  useGetOrgFunctionsQuery,
  useCreateOrgFunctionMutation,
  useUpdateOrgFunctionMutation,
  useDeleteOrgFunctionMutation,
  useGetOrgPartnersQuery,
  useCreateOrgPartnerMutation,
  useUpdateOrgPartnerMutation,
  useDeleteOrgPartnerMutation,
  useSuggestOwnerQuery,
  useGetOwnershipReportQuery,
  useExportOwnershipReportMutation,
} = orgApi;
